// MusicContext.js
import React, { useState, createContext, useEffect, useRef } from "react";
import { Alert, Snackbar } from "@mui/material";
import { getContentArtwork } from "@/utils";
import { postEvent } from "@/services/api";
import { useAudioSafeUrl, useCachedAudioUrl } from "@/services/hooks";

export const MusicContext = createContext();

// A play is only counted once the listener reaches this many seconds of playback.
const PLAY_THRESHOLD_SECONDS = 30;

export const MusicProvider = ({ children }) => {
  const [music, setMusic] = useState([]);

  const [shuffledMusic, setShuffledMusic] = useState([]);
  const [shuffleMode, setShuffleMode] = useState(false);
  const [repeatMode, setRepeatMode] = useState(false); // Now a boolean

  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [pendingPlay, setPendingPlay] = useState(false);
  const [audioError, setAudioError] = useState(null);

  const {
    data: contentUrl,
    isFetching: isAudioLoading,
    isError,
    refetch,
  } = useAudioSafeUrl(currentSong?.key);

  // Read a previously prefetched (usePrefetchAudioUrls) URL synchronously so we
  // can start playback inside a user/media-key gesture without awaiting a fetch.
  const getCachedUrl = useCachedAudioUrl();

  const audioRef = useRef(null);
  // Guards against counting more than one play per listen; reset when a new song
  // is selected or a track rewinds to the start on repeat.
  const hasCountedPlayRef = useRef(false);

  const shuffleArray = (array) => {
    return array
      .map((item) => ({ item, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ item }) => item);
  };

  const toggleShuffleMode = () => {
    setShuffleMode((prevMode) => {
      if (!prevMode) {
        setShuffledMusic(shuffleArray(music)); // Shuffle music when enabling shuffle
      }
      return !prevMode;
    });
  };

  const getCurrentPlaylist = () => (shuffleMode ? shuffledMusic : music);

  const toggleRepeatMode = () => {
    setRepeatMode(!repeatMode); // Toggle between repeating one song and looping the playlist
  };

  const handlePlay = async (content) => {
    console.log("play", content);

    // read fresh so a late-resolving /location (new users) still gates playback
    const isLimited = localStorage.getItem("limited") === "true";
    if (isLimited) {
      const alertMessage = [
        "Pocketbook subscriptions are unable to play music, as we don't",
        "currently have a license to stream music in your country. Licensing",
        "music protects artists and songwriters, which is core to our mission.",
        "We're working hard to get licenses in more countries.",
        "\n\nWith your Pocketbook subscription, you can engage with",
        "notes, forums, media, merch, meetups, and more — but you won't",
        "be able to stream any music.",
      ].join(" ");

      alert(alertMessage);
      return;
    }

    // Same song already selected: resume if URL is loaded, otherwise retry fetch.
    if (currentSong?.key === content.key) {
      const url = contentUrl || getCachedUrl(content.key);
      if (url) {
        if (audioRef.current && !audioRef.current.src) {
          audioRef.current.src = url;
        }
        // isPlaying is driven by the element's `play`/`pause` events below.
        audioRef.current?.play();
        return;
      }
      setAudioError(null);
      setPendingPlay(true);
      refetch();
      return;
    }

    setAudioError(null);
    hasCountedPlayRef.current = false;

    // Fast path: the next track's URL was already prefetched, so set the src and
    // start playback synchronously. This keeps `.play()` inside the originating
    // user/media-key gesture, which iOS standalone PWAs require (a deferred play
    // from an effect is rejected there, advancing the track without audio).
    const cachedUrl = getCachedUrl(content.key);
    if (cachedUrl && audioRef.current) {
      // A fresh src already starts at 0; setting currentTime before metadata
      // loads can throw and would abort the play() below.
      audioRef.current.src = cachedUrl;
      audioRef.current.play().catch(() => {});
      setCurrentSong(content);
      setCurrentTime(0);
      setPendingPlay(false);
      return;
    }

    // Slow path (cold cache): stop the outgoing track immediately so it doesn't
    // keep playing while the next track's URL resolves (which can take several
    // seconds on a cache miss and would otherwise leave song 1 audible under
    // song 2's UI). The useAudioSafeUrl hook fetches the URL (up to ~10s for a
    // cold watermark) and the effect below starts playback once it resolves.
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentSong(content);
    setCurrentTime(0);
    setPendingPlay(true);
  };

  // Start playback once the pending song's URL has resolved, or notify on failure.
  useEffect(() => {
    if (!pendingPlay || isAudioLoading) return;

    if (isError) {
      setAudioError("Unable to play this track. Please try again.");
      setPendingPlay(false);
      return;
    }

    if (contentUrl && audioRef.current) {
      if (audioRef.current.src !== contentUrl) {
        audioRef.current.src = contentUrl;
      }
      audioRef.current
        .play()
        .catch(() => {})
        .finally(() => setPendingPlay(false));
    }
  }, [pendingPlay, contentUrl, isError, isAudioLoading]);

  const handlePause = async () => {
    // isPlaying is driven by the element's `pause` event handler.
    audioRef.current.pause();
  };

  const handleNext = async ({ skipped = true } = {}) => {
    if (skipped) {
      postEvent("next", currentSong.key, { current_time: currentTime });
    }

    const playlist = getCurrentPlaylist();
    const index = playlist.findIndex((song) => song.key === currentSong.key);

    if (repeatMode) {
      // If repeat mode is true, replay the current song from the start
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      setCurrentTime(0);
      hasCountedPlayRef.current = false;
      handlePlay(currentSong);
    } else {
      // Move to the next song, and loop back to the first song if at the end
      const nextIndex = (index + 1) % playlist.length;
      const nextSong = playlist[nextIndex];

      // Single-song playlist that wraps back to itself: treat as a fresh play so
      // a full listen counts again once it passes the threshold.
      if (nextSong.key === currentSong.key) {
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
        setCurrentTime(0);
        hasCountedPlayRef.current = false;
      }

      handlePlay(nextSong);
    }
  };

  const handlePrevious = async () => {
    if (currentTime > 5) {
      audioRef.current.currentTime = 0;
      return;
    }

    const playlist = getCurrentPlaylist();
    const index = playlist.findIndex((song) => song.key === currentSong.key);
    const previousIndex = (index - 1 + playlist.length) % playlist.length;
    const previousSong = playlist[previousIndex];
    handlePlay(previousSong);
  };

  // Records a single play for the current listen, guarded so it fires at most
  // once until the song is reselected or looped.
  const countPlayOnce = (time) => {
    if (!hasCountedPlayRef.current && currentSong) {
      hasCountedPlayRef.current = true;
      postEvent("play", currentSong.key, { current_time: time });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = Math.floor(audioRef.current.currentTime);
      setCurrentTime(time);

      // Count the play once the listener reaches the threshold.
      if (time >= PLAY_THRESHOLD_SECONDS) {
        countPlayOnce(time);
      }

      updatePositionState();
    }
  };

  // Fired by the audio element when the track finishes. Using the native `ended`
  // event is more reliable than comparing floored currentTime against duration
  // (which triggered early and misbehaved for NaN/streamed durations).
  const handleEnded = () => {
    // Reaching the end also counts as a play, covering songs shorter than the
    // threshold.
    countPlayOnce(Math.floor(audioRef.current?.duration) || currentTime);

    if (repeatMode) {
      // If repeatMode is true, replay the same song
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      hasCountedPlayRef.current = false;
      handlePlay(currentSong);
    } else {
      // Otherwise, move to the next song or handle playlist looping
      handleNext({ skipped: false });
    }
  };

  const handleClearSong = () => {
    setCurrentSong(null);
    audioRef.current.pause();
  };

  const handleTimeSliderChange = (e, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
    updatePositionState();
  };

  // Report playback position to the OS so the lockscreen/media controls show and
  // track a scrubber. setPositionState throws on non-finite/zero duration (e.g.
  // streamed tracks before metadata loads), so guard against that.
  const updatePositionState = () => {
    if (!("mediaSession" in navigator) || !audioRef.current) return;
    const dur = audioRef.current.duration;
    if (!Number.isFinite(dur) || dur <= 0) return;
    const pos = Math.min(audioRef.current.currentTime, dur);
    navigator.mediaSession.setPositionState({
      duration: dur,
      playbackRate: audioRef.current.playbackRate || 1,
      position: Math.max(pos, 0),
    });
  };

  useEffect(() => {
    if ("mediaSession" in navigator && currentSong) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentSong?.meta["title"],
        artist: currentSong?.owner.name,
        album: "Notebook",
        artwork: [
          {
            src: getContentArtwork(currentSong),
          },
        ],
      });

      navigator.mediaSession.setActionHandler("play", () => {
        audioRef.current.play();
      });
      navigator.mediaSession.setActionHandler("pause", () => {
        audioRef.current.pause();
      });
      navigator.mediaSession.setActionHandler("previoustrack", handlePrevious);
      navigator.mediaSession.setActionHandler("nexttrack", handleNext);

      navigator.mediaSession.setActionHandler("seekto", (details) => {
        if (!audioRef.current) return;
        if (details.fastSeek && "fastSeek" in audioRef.current) {
          audioRef.current.fastSeek(details.seekTime);
        } else {
          audioRef.current.currentTime = details.seekTime;
        }
        setCurrentTime(details.seekTime);
        updatePositionState();
      });
    }
    // Re-register so next/prev capture the current repeat/shuffle/time state
    // instead of stale values from when the song was first selected.
  }, [currentSong, repeatMode, shuffleMode, shuffledMusic, music, currentTime]);

  return (
    <MusicContext.Provider
      value={{
        setMusic,
        music,
        currentSong,
        isPlaying,
        isAudioLoading,
        setIsPlaying,
        handlePause,
        handlePlay,
        handleNext,
        handlePrevious,
        currentTime,
        duration,
        handleClearSong,
        handleTimeSliderChange,
        repeatMode,
        toggleRepeatMode,
        shuffleMode,
        toggleShuffleMode,
      }}
    >
      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={() => {
          setDuration(audioRef.current.duration);
          updatePositionState();
        }}
      />

      <Snackbar
        open={!!audioError}
        autoHideDuration={10000}
        onClose={() => setAudioError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAudioError(null)}
          severity="error"
          variant="filled"
        >
          {audioError}
        </Alert>
      </Snackbar>

      {children}
    </MusicContext.Provider>
  );
};
